import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect
} from "react";
import { UserData } from "@/entities/users/types";
import SalesPipeline from "./SalesPipeline";
import { useReport } from "@/entities/reporting/hooks";
import {
  PeerComparisonData,
  PipelineFunnelData,
  ReportType,
  SettingsPeriod,
  StageData
} from "@/entities/reporting/types";
import { useUsers } from "@/entities/users/hooks";
import { useTranslation } from "react-i18next";
import { useDealStages } from "@/entities/settings/deal/hooks";
import {
  debounce,
  flatMap,
  isEmpty,
  keyBy,
  keys,
  mapValues,
  sumBy,
  uniq
} from "lodash";

interface Props {
  userData?: UserData;
  isAE: boolean;
}

const SalesPipelineWrapper: React.FC<Props> = ({ userData, isAE }) => {
  const { t } = useTranslation();

  const stages = useDealStages().data?.stages;
  const [reportPeriod, setReportPeriod] = useState(SettingsPeriod.ALL_TIMES);

  // Keep track of the last successful data using refs to avoid re-render loops
  // Using refs instead of state because we only need to preserve the value,
  // not trigger re-renders when it changes
  const lastValidReportRef = useRef<PipelineFunnelData | null>(null);
  const lastValidComparisonRef = useRef<PeerComparisonData | null>(null);

  const { report, isLoading } = useReport(
    ReportType.PIPELINE_FUNNEL,
    reportPeriod
  );

  const debouncedPeriodSelect = useCallback(
    debounce((period: SettingsPeriod) => {
      setReportPeriod(period);
    }, 300),
    []
  );

  const currentFunnelReport = report?.salesPipelineFunnel?.funnel ?? undefined;
  const currentComparisonReport =
    report?.salesPipelineFunnel?.peerComparison ?? undefined;

  useEffect(() => {
    if (currentFunnelReport) {
      lastValidReportRef.current = currentFunnelReport;
    }
  }, [currentFunnelReport]);

  useEffect(() => {
    if (currentComparisonReport) {
      lastValidComparisonRef.current = currentComparisonReport;
    }
  }, [currentComparisonReport]);

  // Use current data if available, otherwise fall back to last valid data
  const salesFunnelReport: PipelineFunnelData =
    currentFunnelReport ?? lastValidReportRef.current ?? undefined;

  const peerComparisonReport: PeerComparisonData =
    currentComparisonReport ?? lastValidComparisonRef.current ?? undefined;

  const userIds = useMemo(() => {
    if (!salesFunnelReport) return [];
    return uniq(
      flatMap(salesFunnelReport.stages, (stage) => keys(stage.contributors))
    );
  }, [salesFunnelReport]);

  const users = useUsers(userIds).users;

  const processedStages = useMemo(() => {
    if (!report?.salesPipelineFunnel || !stages?.length || isEmpty(users)) {
      return undefined;
    }

    // Create users map for efficient lookups
    const usersMap = keyBy(users, (user) => user.id.toString());

    // Calculate the total for each stage first
    const stageTotals = salesFunnelReport.stages.map((stage) =>
      sumBy(Object.values(stage.contributors), "value")
    );
    const stageWeightedTotals = salesFunnelReport.stages.map((stage) =>
      sumBy(Object.values(stage.contributors), "weightedValue")
    );

    const mappedStages = salesFunnelReport.stages.map(
      (stage, index): StageData => {
        const stageInfo = stages.find((s) => s.id === stage.stageId);
        return {
          ...stage,
          stageName: stageInfo?.name ?? t("unknown"),
          stageOrder: stageInfo?.stageOrder ?? 0,
          totalValue: stageTotals[index],
          totalWeightedValue: stageWeightedTotals[index],
          contributors: mapValues(stage.contributors, (value, userId) => {
            const user = usersMap[userId];
            return {
              ...value,
              user
            };
          })
        };
      }
    );

    return mappedStages.sort((a, b) => a.stageOrder - b.stageOrder);
  }, [stages, report, users.length, t, salesFunnelReport]);

  const processedReport = useMemo(() => {
    if (!report?.salesPipelineFunnel || !processedStages) return;
    return {
      ...report.salesPipelineFunnel,
      stages: processedStages
    };
  }, [report, processedStages]);

  return (
    <SalesPipeline
      pipelineFunnelChartProps={processedReport}
      comparisonChartData={peerComparisonReport}
      userData={userData}
      periodSelect={debouncedPeriodSelect}
      isLoading={isLoading}
      isAE={isAE}
    />
  );
};

export default SalesPipelineWrapper;
